// useRequirePerm — the one way a screen asks "may this user do this?".
//
// Copied from the pattern Table Orders has used all along: the button stays
// where it is, and a user without the switch is TOLD on the tap rather than
// left hunting for a button that vanished. A cashier who cannot find "Place
// Order" calls support; one who is told "your admin has turned this off"
// calls the admin.
//
// Fail-open is inherited from PermissionProvider: a user with no permission
// codes at all (a company that never set up roles) is allowed everything, so
// nothing changes for them the day a new switch ships.
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePermission, decidePermission, readKnownCodes, isOptInCode } from './PermissionProvider';

export const DENIED_MESSAGE =
  'You do not have permission for this action. Ask your admin to turn it on for you.';

// Names the switch, so the admin knows what to turn on under Users → User
// Permissions (search the code there) instead of guessing which of a page's
// twenty switches said no.
export const deniedMessage = (code) =>
  code
    ? `You do not have permission for this action (${code}). Ask your admin to turn it on under Users → User Permissions.`
    : DENIED_MESSAGE;

export function useRequirePerm() {
  const { hasPermission, hasAnyPermission, useGranularRbac } = usePermission();

  const requirePerm = useCallback(
    (code, message) => {
      if (hasPermission(code)) return true;
      toast.error(message || deniedMessage(code));
      return false;
    },
    [hasPermission],
  );

  return { requirePerm, hasPermission, hasAnyPermission, useGranularRbac };
}

// Outside React (a plain function such as a tab list builder): the same
// answer, by the same rule (decidePermission), from the same sessionStorage
// the provider reads. Empty list = allowed; a code the database does not
// have = allowed.
export function hasStoredPermission(code) {
  if (!code) return true;
  // An opt-in switch is OFF unless held - the empty-list / nothing-stored
  // fail-open below does not apply to it.
  const optIn = isOptInCode(code);
  try {
    const raw = sessionStorage.getItem('permissionCodes');
    if (!raw) return !optIn;
    const list = JSON.parse(raw);
    if (!Array.isArray(list) || list.length === 0) return !optIn;
    const codes = list
      .map((x) => (typeof x === 'string' ? x : x?.code ?? x?.Code ?? ''))
      .map((c) => String(c).trim())
      .filter(Boolean);
    return decidePermission(code, codes, readKnownCodes());
  } catch {
    return true;
  }
}
